import {
  Text,
  UnstyledButton,
  Flex,
  Burger,
  Button,
  ActionIcon,
  Tooltip,
  Indicator
} from '@mantine/core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { CashierMachineIcon } from '../../assets/icons/cashier-machine_icon';
import { useLocation } from 'react-router-dom';
import classes from './HeaderMenu.module.css';
import { IconClock, IconHome, IconQrcode } from '@tabler/icons-react';
import { useSocketContext } from '../../app/context/socketContext';
import { useGetRequestedOrdersSummary } from '../../hooks/orders/queries';
import { useGetStoreById, useGetStores } from '../../hooks/stores/queries';
import { useGetAttendanceStatusHourly, useClockIn, useClockOut } from '../../hooks/attendance';
import { ScanOrderModal } from '../ScanOrderModal';
import { useDisclosure } from '@mantine/hooks';
import { StoreUserMenu } from './StoreUserMenu';
import { useSelector } from 'react-redux';
import { CompanyPlanLevel } from '../../enum/CompanyPlanLevelEnum';
import useCanUseAttendanceModule from '../../hooks/useCanUseAttendanceModule';
import { UpdatesButton } from './UpdatesButton';
import InitialCashPrompt from '../../pages/NewOrder/components/InitialCashPrompt';
import { hasPermission } from '../../utils/permission-utils';

interface HeaderMenuProps {
  menuCollapsedMobile: boolean;
  menuCollapsedDesktop: boolean;
  toggleMobile: any;
  toggleDesktop: any;
}

export function HeaderMenuComponent({
  menuCollapsedMobile,
  menuCollapsedDesktop,
  toggleMobile,
  toggleDesktop
}: HeaderMenuProps) {
  const history = useHistory();
  const { t } = useTranslation(['MenuOptions', 'Attendance']);
  const { user, company } = useSelector((state: any) => state.user);
  const location = useLocation();
  const { socket } = useSocketContext();
  const [opened, { open, close }] = useDisclosure(false);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId: string | null = searchParams.get('storeId');
  const { data: stores } = useGetStores();
  const storeIds = useMemo(() => {
    // needed this way to prevent refetching of requestedOrderSummaryData when navigating or re-rendering
    if (!stores) return '';
    return stores
      .map((s: any) => s._id)
      .sort()
      .join(','); // ordena para que siempre sea estable
  }, [stores]);
  const { data: requestedOrderSummaryData, refetch: refetchRequestedOrdersSummary } =
    useGetRequestedOrdersSummary(storeIds);
  const totalRequestedOrdes = useMemo(() => {
    return (
      requestedOrderSummaryData?.reduce((prev: number, curr) => {
        return prev + curr.count;
      }, 0) || 0
    );
  }, [requestedOrderSummaryData]);
  const [userClockInStore, setUserClockInStore] = useState('');
  const [isInitialCashPromptOpen, setIsInitialCashPromptOpen] = useState(false);
  const showJoinNowButton = useMemo(() => {
    return company?.planLevel === CompanyPlanLevel.TRIAL && user?.isCompanyAdmin;
  }, [company, user]);

  useEffect(() => {
    // ASSIGNED IN THE USE EFFECT SO WE CAN CLOCK IN AND CLOCK OUT FROM THE SAME STORE
    // If the user is a company admin, use the searchParamStoreId (if it's not already set)
    if (userClockInStore === '' && user?.isCompanyAdmin && searchParamStoreId) {
      setUserClockInStore(searchParamStoreId);
    }
    // If the user is a store user, use the user's store
    if (userClockInStore === '' && user?.store) {
      setUserClockInStore(user?.store);
    }
  }, [user, searchParamStoreId]);

  // 🕐 Attendance status hook
  const { canUseAttendanceModule } = useCanUseAttendanceModule(userClockInStore || '');
  const { data: activeStore } = useGetStoreById(!!userClockInStore, userClockInStore || '');
  const { data: attendanceStatus, isLoading: attendanceLoading } = useGetAttendanceStatusHourly(
    !!userClockInStore && !!user?._id && canUseAttendanceModule,
    {
      storeId: userClockInStore || '',
      userId: user?._id || ''
    }
  );

  // 🕐 Attendance mutations
  const clockInMutation = useClockIn();
  const clockOutMutation = useClockOut(userClockInStore || '');

  // 🛡️ Anti-spam protection
  const lastClickRef = useRef<number>(0);
  const [isInCooldown, setIsInCooldown] = useState(false);
  const COOLDOWN_MS = 3000; // 3 segundos entre clicks

  // 🕐 Attendance handlers with debounce protection
  const handleAttendanceClick = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickRef.current;

    // 🚫 Prevenir spam - cooldown de 3 segundos
    if (timeSinceLastClick < COOLDOWN_MS) {
      const secondsLeft = Math.ceil((COOLDOWN_MS - timeSinceLastClick) / 1000);
      console.warn(`Anti-spam: Espera ${secondsLeft}s más`);

      // 🎯 Activar estado visual de cooldown
      if (!isInCooldown) {
        setIsInCooldown(true);
        setTimeout(() => setIsInCooldown(false), COOLDOWN_MS - timeSinceLastClick);
      }
      return;
    }

    if (!user?._id || !userClockInStore) {
      console.warn('Missing userId or storeId for attendance operation');
      return;
    }

    // 🔒 Marcar timestamp del click
    lastClickRef.current = now;

    try {
      if (attendanceStatus?.active) {
        // Clock-out
        await clockOutMutation.mutateAsync({
          userId: user?._id,
          storeId: userClockInStore || '',
          notes: 'Clock-out desde header' // TODO: Remove this notes
        });
      } else {
        // Clock-in
        await clockInMutation.mutateAsync({
          storeId: userClockInStore || '',
          userId: user?._id || '',
          notes: 'Clock-in desde header' // TODO: Remove this note
        });

        if (activeStore?.attendanceConfig?.promptInitialCashOnShiftStart && user?._id) {
          setIsInitialCashPromptOpen(true);
        }
      }
    } catch (error) {
      console.error('Error en operación de attendance:', error);
      // Resetear timestamp en caso de error para permitir retry inmediato
      lastClickRef.current = 0;
    }
  }, [
    user?._id,
    userClockInStore,
    searchParamStoreId,
    attendanceStatus?.active,
    activeStore?.attendanceConfig?.promptInitialCashOnShiftStart,
    clockInMutation,
    clockOutMutation
  ]);

  const navigateToPage = (pageURL: string) => {
    history.push({
      pathname: pageURL,
      state: { storeID: searchParamStoreId },
      search: `?storeId=${searchParamStoreId}`
    });
  };

  const navigateToCashupReport = () => {
    const cashupList = '/cashUp';
    const cashupCreate = `/cashUp/create`;
    // WE ARE REDIRECTING THE USER TO CREATE PAGE IF THEY DON'T HAVE PERMISSION TO VIEW THE LIST
    if (hasPermission(user, 'cashupReports', 'canView')) {
      history.push({
        pathname: cashupList,
        state: { storeID: searchParamStoreId },
        search: `?storeId=${searchParamStoreId}`
      });
    } else if (hasPermission(user, 'cashupReports', 'canCreate')) {
      history.push({
        pathname: cashupCreate,
        state: { storeID: searchParamStoreId },
        search: `?storeId=${searchParamStoreId}`
      });
    }
  };

  const onJoinClick = () => {
    let stripeID = cookies.get('stripeCustomerId');
    history.push(`/subscribe?customer=${stripeID}`);
  };

  const handleMessageReceived = useCallback(
    (data: any) => {
      refetchRequestedOrdersSummary();
    },
    [refetchRequestedOrdersSummary]
  );

  useEffect(() => {
    if (!socket) return;

    // Register the event listener
    socket.on('mobile-order-requested', handleMessageReceived);

    // Cleanup function to remove the event listener
    return () => {
      // socket.off('mobile-order-requested', handleMessageReceived);
    };
  }, [socket, handleMessageReceived]);

  return (
    <Flex align={'center'} w={'100%'}>
      <Burger
        mx={10}
        opened={menuCollapsedMobile}
        onClick={toggleMobile}
        hiddenFrom="sm"
        size="sm"
      />
      <Burger
        mx={10}
        opened={menuCollapsedDesktop}
        onClick={toggleDesktop}
        visibleFrom="sm"
        size="md"
      />
      <Flex justify={'space-evenly'} align={'center'} flex={1} visibleFrom="sm">
        <UnstyledButton
          className={classes.control}
          data-active={'/new-order' === location.pathname || '/' === location.pathname}
          onClick={() => navigateToPage('/')}>
          <Text fw={'bold'}>{t('MenuOptions:newOrder')}</Text>
        </UnstyledButton>
        <UnstyledButton
          className={classes.control}
          data-active={'/cleaning' === location.pathname}
          onClick={() => navigateToPage('/cleaning')}>
          <Text fw={'bold'}>{t('MenuOptions:clean')}</Text>
        </UnstyledButton>
        {company?.companyServicesPermissions?.enableOutsourcing ? (
          <UnstyledButton
            className={classes.control}
            data-active={'/outsourcing' === location.pathname}
            onClick={() => navigateToPage('/outsourcing')}>
            <Text fw={'bold'}>{t('MenuOptions:outsourcing')}</Text>
          </UnstyledButton>
        ) : null}
        <UnstyledButton
          className={classes.control}
          data-active={'/ready' === location.pathname}
          onClick={() => navigateToPage('/ready')}>
          <Text fw={'bold'}>{t('MenuOptions:ready')}</Text>
        </UnstyledButton>
        <UnstyledButton
          className={classes.control}
          data-active={'/pickup' === location.pathname}
          onClick={() => navigateToPage('/pickup')}>
          <Text fw={'bold'}>{t('MenuOptions:pickups')}</Text>
        </UnstyledButton>
      </Flex>
      <Flex gap={10} align={'center'} justify={'center'} wrap={'wrap'}>
        <Tooltip label={t('ScanOrderModal:scanOrderTooltip')}>
          <ActionIcon
            radius={'none'}
            variant="default"
            aria-label="Escanear QR de Pedido"
            size={'lg'}
            onClick={open}>
            <IconQrcode
              style={{
                width: '70%',
                height: '70%'
              }}
              stroke={'1.5'}
            />
          </ActionIcon>
        </Tooltip>
        {company?.companyServicesPermissions?.canUseCustomersApp && (
          <Tooltip label="Pedidos solicitados desde app">
            <Indicator processing position="top-end" disabled={totalRequestedOrdes <= 0}>
              <ActionIcon
                radius={'none'}
                variant="default"
                aria-label="Pedidos solicitados desde app"
                size={'lg'}
                onClick={() => navigateToPage('/requested-orders')}>
                <IconHome
                  style={{
                    width: '70%',
                    height: '70%',
                    color: socket?.connected ? 'green' : 'red'
                  }}
                  stroke={'1.5'}
                />
              </ActionIcon>
            </Indicator>
          </Tooltip>
        )}
        <UpdatesButton />
        <Tooltip label="Corte de caja">
          <ActionIcon
            radius={'none'}
            variant="default"
            aria-label="hacer-corte-caja"
            size={'lg'}
            onClick={() => navigateToCashupReport()}>
            <CashierMachineIcon style={{ width: '70%', height: '70%' }} stroke={'1.5'} />
          </ActionIcon>
        </Tooltip>
        {canUseAttendanceModule ? (
          <Tooltip
            label={
              isInCooldown
                ? '⏱️ Espera unos segundos antes del siguiente click'
                : clockInMutation.isPending || clockOutMutation.isPending
                ? t('Attendance:processing')
                : attendanceLoading
                ? t('Attendance:loadingStatus')
                : attendanceStatus?.active
                ? t('Attendance:clickToClockOut', {
                    clockInTime: new Date(attendanceStatus.clockInTime || '').toLocaleTimeString()
                  })
                : t('Attendance:notClockedIn')
            }>
            <ActionIcon
              radius={'none'}
              variant="default"
              aria-label="attendance-clock-toggle"
              size={'lg'}
              onClick={handleAttendanceClick}
              disabled={
                isInCooldown ||
                clockInMutation.isPending ||
                clockOutMutation.isPending ||
                attendanceLoading ||
                !user?._id ||
                !userClockInStore
              }
              style={{
                backgroundColor: attendanceStatus?.active ? '#e8f5e8' : '#fdf2f2',
                borderColor: attendanceStatus?.active ? '#22c55e' : '#ef4444',
                borderWidth: '2px',
                opacity:
                  isInCooldown ||
                  clockInMutation.isPending ||
                  clockOutMutation.isPending ||
                  !user?._id ||
                  !userClockInStore
                    ? 0.6
                    : 1
              }}>
              <IconClock
                style={{
                  width: '70%',
                  height: '70%',
                  color: attendanceStatus?.active ? '#15803d' : '#dc2626'
                }}
                stroke={'1.5'}
              />
            </ActionIcon>
          </Tooltip>
        ) : null}
        <StoreUserMenu />
      </Flex>
      <InitialCashPrompt
        opened={isInitialCashPromptOpen}
        onClose={() => setIsInitialCashPromptOpen(false)}
        storeId={userClockInStore || ''}
        userId={user?._id || ''}
        currencySymbol={activeStore?.currencySymbol}
      />
      {showJoinNowButton ? (
        <Button mr={10} flex={'none'} size="sm" onClick={onJoinClick}>
          {t('MenuOptions:joinNow').toUpperCase()}
        </Button>
      ) : null}
      <ScanOrderModal opened={opened} onClose={close} />
    </Flex>
  );
}

export const HeaderMenu = memo(HeaderMenuComponent);
